import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Star, ThumbsUp, ThumbsDown, Minus } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

interface Survey {
  id: string
  type: string
  score: number
  feedback: string | null
  ticketId: string | null
  respondentEmail: string | null
  respondedAt: string | null
  createdAt: string
}

interface Stats {
  total: number
  avgCsat: number
  npsScore: number
  responseRate: number
  promoters: number
  passives: number
  detractors: number
}

function npsCategory(score: number) {
  if (score >= 9) return { label: 'Promoter', icon: ThumbsUp, color: 'text-green-500' }
  if (score >= 7) return { label: 'Passive', icon: Minus, color: 'text-yellow-500' }
  return { label: 'Detractor', icon: ThumbsDown, color: 'text-red-500' }
}

function CreateSurveyDialog() {
  const [open, setOpen] = useState(false)
  const [type, setType] = useState('csat')
  const [score, setScore] = useState('')
  const [feedback, setFeedback] = useState('')
  const [email, setEmail] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/csat/surveys', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['csat'] })
      toast({ title: 'Survey recorded' })
      setOpen(false)
      setScore(''); setFeedback(''); setEmail('')
    },
    onError: () => toast({ title: 'Error', description: 'Failed to record survey', variant: 'destructive' }),
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button><Plus className="mr-2 h-4 w-4" /> Record Response</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Record Survey Response</DialogTitle><DialogDescription>Record a CSAT or NPS survey response.</DialogDescription></DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="csat">CSAT (1-5)</SelectItem>
                <SelectItem value="nps">NPS (0-10)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Score ({type === 'csat' ? '1-5' : '0-10'})</Label>
            <Input type="number" min={type === 'csat' ? 1 : 0} max={type === 'csat' ? 5 : 10} value={score} onChange={(e) => setScore(e.target.value)} />
          </div>
          <div className="space-y-2"><Label>Email</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="respondent@example.com" /></div>
          <div className="space-y-2">
            <Label>Feedback</Label>
            <textarea className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm" value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Optional feedback..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={() => createMutation.mutate({ type, score: parseInt(score), feedback: feedback || undefined, respondentEmail: email || undefined })} disabled={!score || createMutation.isPending}>
            {createMutation.isPending ? 'Recording...' : 'Record'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function CsatPage() {
  const [search, setSearch] = useState('')

  const { data: surveysData, isLoading } = useQuery({
    queryKey: ['csat', 'surveys'],
    queryFn: () => apiClient.get<{ surveys: Survey[]; pagination: unknown }>('/csat/surveys'),
  })

  const { data: statsData } = useQuery({
    queryKey: ['csat', 'stats'],
    queryFn: () => apiClient.get<Stats>('/csat/stats'),
  })

  const surveys = surveysData?.data?.surveys || []
  const stats = statsData?.data
  const filtered = surveys.filter((s) => s.respondentEmail?.toLowerCase().includes(search.toLowerCase()) || s.feedback?.toLowerCase().includes(search.toLowerCase()))

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">CSAT / NPS</h1>
          <p className="text-muted-foreground">Customer satisfaction and Net Promoter Score tracking</p>
        </div>
        <CreateSurveyDialog />
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{stats?.avgCsat?.toFixed(1) || '-'}</div><p className="text-xs text-muted-foreground">Avg CSAT (out of 5)</p></CardContent></Card>
        <Card><CardContent className="pt-6"><div className={`text-2xl font-bold ${(stats?.npsScore || 0) >= 50 ? 'text-green-500' : (stats?.npsScore || 0) >= 0 ? 'text-yellow-500' : 'text-red-500'}`}>{stats?.npsScore ?? '-'}</div><p className="text-xs text-muted-foreground">NPS Score</p></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{stats?.total || 0}</div><p className="text-xs text-muted-foreground">Total Responses</p></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{stats?.responseRate ? `${(stats.responseRate * 100).toFixed(0)}%` : '-'}</div><p className="text-xs text-muted-foreground">Response Rate</p></CardContent></Card>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search responses..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Star className="h-5 w-5" /> Responses</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground"><Star className="h-12 w-12 mx-auto mb-4 opacity-50" /><p>No survey responses yet</p></div>
          ) : (
            <div className="space-y-2">
              {filtered.map((survey) => {
                const cat = survey.type === 'nps' ? npsCategory(survey.score) : null
                return (
                  <div key={survey.id} className="flex items-center gap-4 p-3 rounded-lg border border-border hover:bg-muted/50">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-muted font-bold">
                      {survey.score}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{survey.type.toUpperCase()}</Badge>
                        {cat && <span className={`text-xs flex items-center gap-1 ${cat.color}`}><cat.icon className="h-3 w-3" />{cat.label}</span>}
                      </div>
                      {survey.feedback && <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{survey.feedback}</p>}
                      <p className="text-xs text-muted-foreground mt-1">{survey.respondentEmail || 'Anonymous'} &middot; {new Date(survey.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
